/**
 * Which of Badger's expression frames shows (docs/features/badger-cohost.md, Identity): listening still at rest; while
 * he speaks, the mouth frames follow his voice level (or a gentle cadence where the level can't be read); the working
 * frame while he is thinking; under reduced motion, the listening frame alone, the speaking ring saying he talks.
 * States show as motion, never as a new colour.
 */

import { useEffect, useState } from 'react'
import { createAudioAnalyser, type RemoteAudioTrack } from 'livekit-client'
import type { CohostState } from './useCohost'
import { useMediaQuery } from './useMediaQuery'

const FRAME_DIR = '/brand/play/badger'

/** His frames (transparent WebP, 256 px), each a masked edit of the listening render, so they swap without shimmer. */
export const BADGER_FRAME_URLS = {
  listening: `${FRAME_DIR}/badger-listening.webp`,
  'speaking-1': `${FRAME_DIR}/badger-speaking-1.webp`,
  'speaking-2': `${FRAME_DIR}/badger-speaking-2.webp`,
  working: `${FRAME_DIR}/badger-working.webp`,
  laughing: `${FRAME_DIR}/badger-laughing.webp`,
} as const

export type BadgerFrame = keyof typeof BADGER_FRAME_URLS
export const BADGER_FRAMES = Object.keys(BADGER_FRAME_URLS) as BadgerFrame[]

/** How open his mouth is: closed (the listening frame), the small talking mouth, or the wide "ah!". */
export type BadgerMouth = 'closed' | 'open' | 'wide'

const MOUTH_FRAME: Record<BadgerMouth, BadgerFrame> = { closed: 'listening', open: 'speaking-1', wide: 'speaking-2' }

/** How often the mouth moves: about eight times a second, a syllable's pace. */
export const MOUTH_MS = 120

/** The mouth's beat while his level can't be read: talking, "ah!", talking, closed. */
export const MOUTH_CADENCE: readonly BadgerMouth[] = ['open', 'wide', 'open', 'closed']

/** Below this level (0–1) he is between words. */
export const SILENT_LEVEL = 0.02
/** The least a recent peak counts for, so a quiet hiss isn't scaled up into a wide mouth. */
export const MIN_PEAK = 0.08
/** How fast the recent peak falls back each reading, so a loud word doesn't keep his mouth small for long. */
export const PEAK_DECAY = 0.96

/** The mouth for a voice level against its recent peak: shut between words, open, wide on the loudest syllables. */
export function mouthFor(level: number, peak: number): BadgerMouth {
  if (!(level >= SILENT_LEVEL)) return 'closed'
  const share = level / Math.max(peak, MIN_PEAK)
  if (share < 0.3) return 'closed'
  return share < 0.7 ? 'open' : 'wide'
}

export interface BadgerFrameInput {
  state: CohostState | null
  speaking: boolean
  mouth: BadgerMouth
  reducedMotion: boolean
}

/** The frame for his state: thinking wins (the tool hasn't returned), then his voice, else listening. */
export function badgerFrame({ state, speaking, mouth, reducedMotion }: BadgerFrameInput): BadgerFrame {
  if (reducedMotion) return 'listening'
  if (state === 'thinking') return 'working'
  return speaking ? MOUTH_FRAME[mouth] : 'listening'
}

/** The mouth from the cadence while `active`, closed otherwise. */
function useCadence(active: boolean): BadgerMouth {
  const [beat, setBeat] = useState(0)
  useEffect(() => {
    if (!active) {
      setBeat(0)
      return
    }
    const timer = window.setInterval(() => setBeat((last) => (last + 1) % MOUTH_CADENCE.length), MOUTH_MS)
    return () => window.clearInterval(timer)
  }, [active])
  return active ? MOUTH_CADENCE[beat] : 'closed'
}

/** The analyser's ear: a short window over speech's loudness range, lightly smoothed so the mouth keeps up. */
const ANALYSER = { fftSize: 256, smoothingTimeConstant: 0.3, minDecibels: -90, maxDecibels: -25 }

/**
 * The mouth his voice makes, read from his track every `MOUTH_MS` while `active`. Null when the level can't be read: no
 * track, no Web Audio, or an audio context the browser keeps suspended (no gesture yet, iOS), when the cadence stands in.
 * The page plays his voice through the room's renderer; this only listens, so it never plays it twice.
 */
function useVoiceMouth(track: RemoteAudioTrack | null, active: boolean): BadgerMouth | null {
  const [mouth, setMouth] = useState<BadgerMouth | null>(null)
  useEffect(() => {
    if (!active || !track) {
      setMouth(null)
      return
    }
    let analyser: ReturnType<typeof createAudioAnalyser>
    try {
      analyser = createAudioAnalyser(track, ANALYSER)
    } catch {
      setMouth(null)
      return
    }
    const context = analyser.analyser.context as AudioContext
    void context.resume?.().catch(() => {})
    let peak = MIN_PEAK
    const timer = window.setInterval(() => {
      if (context.state !== 'running') {
        setMouth(null)
        return
      }
      const level = analyser.calculateVolume()
      peak = Math.max(level, peak * PEAK_DECAY, MIN_PEAK)
      setMouth(mouthFor(level, peak))
    }, MOUTH_MS)
    return () => {
      window.clearInterval(timer)
      void analyser.cleanup().catch(() => {})
    }
  }, [track, active])
  return active ? mouth : null
}

export interface BadgerFrameOptions {
  state: CohostState | null
  speaking: boolean
  /** His audio track while this tab receives it: the mouth follows its level. */
  track?: RemoteAudioTrack | null
}

/** The frame to show now: his voice's level (or the cadence) moves the mouth; reduced motion keeps him listening. */
export function useBadgerFrame({ state, speaking, track = null }: BadgerFrameOptions): BadgerFrame {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const talking = speaking && !reducedMotion && state !== 'thinking'
  const heard = useVoiceMouth(track, talking)
  const beat = useCadence(talking && heard === null)
  return badgerFrame({ state, speaking, mouth: heard ?? beat, reducedMotion })
}
